/**
 * DOM-level helpers for rendering NIP-84 highlights as `<mark>` spans
 * over already-rendered wiki content (Djot/AsciiDoc/Markdown/plain-text
 * — whichever renderer produced the DOM), without needing to teach any
 * of those renderers about highlights directly.
 *
 * This is deliberately DOM-based rather than React-tree-based: NIP-84
 * stores only the highlighted *text itself* in `content` — the spec has
 * no position/offset anchors — so "finding where a highlight goes" is
 * inherently a text-search problem against whatever text ends up
 * on-screen, regardless of which markup format produced it. The
 * standard, well-established technique for this (used by e.g. the
 * Hypothesis web annotator) is: walk the rendered container's text
 * nodes, search the concatenated text for the highlight string, then
 * split and wrap the matching text node(s) in a `<mark>` — which is
 * exactly what this module does.
 *
 * Because this mutates a subtree React also manages (the rendered
 * article body), callers MUST call `unwrapHighlightMarks` before every
 * re-application (and whenever the underlying content re-renders) to
 * restore the original text nodes first — otherwise repeated effect
 * runs would double-wrap, and React's own reconciliation of the
 * surrounding tree would never know these `<mark>` elements exist.
 */

export const HIGHLIGHT_MARK_ATTR = 'data-nip84-highlight';

interface TextSpan {
  node: Text;
  start: number;
  end: number;
}

function getTextNodes(root: Node): Text[] {
  const nodes: Text[] = [];
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  let n: Node | null;
  while ((n = walker.nextNode())) {
    nodes.push(n as Text);
  }
  return nodes;
}

function buildSpans(root: HTMLElement): { fullText: string; spans: TextSpan[] } {
  const nodes = getTextNodes(root);
  let full = '';
  const spans: TextSpan[] = [];
  for (const node of nodes) {
    const text = node.textContent ?? '';
    const start = full.length;
    full += text;
    spans.push({ node, start, end: start + text.length });
  }
  return { fullText: full, spans };
}

/** Removes every previously-applied highlight `<mark>` in `container`, restoring the original text. */
export function unwrapHighlightMarks(container: HTMLElement): void {
  const marks = container.querySelectorAll(`mark[${HIGHLIGHT_MARK_ATTR}]`);
  marks.forEach((mark) => {
    const parent = mark.parentNode;
    if (!parent) return;
    while (mark.firstChild) {
      parent.insertBefore(mark.firstChild, mark);
    }
    parent.removeChild(mark);
  });
  container.normalize();
}

/** Splits and wraps the text node(s) covering global text offsets `[start, end)` in one or more `<mark>` elements, configured via `configure`. */
function wrapRange(
  spans: TextSpan[],
  start: number,
  end: number,
  configure: (mark: HTMLElement) => void,
): boolean {
  const overlapping = spans.filter((s) => s.end > start && s.start < end);
  if (overlapping.length === 0) return false;

  for (const span of overlapping) {
    const node = span.node;
    const text = node.textContent ?? '';
    const localStart = Math.max(0, start - span.start);
    const localEnd = Math.min(text.length, end - span.start);
    if (localStart >= localEnd) continue;

    // Split off the trailing remainder first so `localStart`/`localEnd`
    // offsets into `node` stay valid for the second split.
    if (localEnd < text.length) {
      node.splitText(localEnd);
    }
    const target = localStart > 0 ? node.splitText(localStart) : node;

    const mark = document.createElement('mark');
    configure(mark);
    target.parentNode?.insertBefore(mark, target);
    mark.appendChild(target);
  }
  return true;
}

/**
 * Finds the first occurrence of `text` within `container`'s rendered
 * text and wraps it in a configured `<mark>`. Returns `false` (no-op)
 * if the text can't be found — e.g. the underlying content changed
 * since the highlight was created, or a markup renderer normalized
 * something (smart quotes, etc.) so the exact string no longer
 * appears. This is an accepted best-effort limitation of text-anchored
 * highlighting, not a bug: NIP-84 provides no other way to locate a
 * highlight within its source.
 */
export function applyHighlightMark(
  container: HTMLElement,
  text: string,
  configure: (mark: HTMLElement) => void,
): boolean {
  if (!text) return false;
  const { fullText, spans } = buildSpans(container);
  const index = fullText.indexOf(text);
  if (index === -1) return false;
  return wrapRange(spans, index, index + text.length, configure);
}
